import { Flex, Heading, Link, Text } from "@chakra-ui/react";
import TransactionModal from "@components/TransactionModal";
import {
  CLAIM_DISCLAIMER_APPROVE,
  CLAIM_DISCLAIMER_REJECT,
  CLAIM_EL_FAILED,
  CLAIM_EL_SUCCESSFUL,
  CLAIM_SCROLL_FAILED,
  CLAIM_SCROLL_SUCCESSFUL,
} from "@constants/analytics";
import { TXN_TYPES } from "@constants/common";
import useDappChain from "@hooks/useDappChain";
import { useState } from "react";
import { isErrorRejectedByUser, trackEvent } from "utils";
import ClaimELDisclaimer from "./Disclaimer";
import airdrops from "files/airdrops.json";
import ActiveAirDrops from "./ActiveAirdrops";
import useClaims, { Airdrop, ClaimData, ClaimType } from "@hooks/useClaims";
import FutureAirdrops from "./FutureAirdrops";
import ClaimedAirdrops from "./ClaimedAirdrops";
import { useAccount, usePublicClient, useWriteContract } from "wagmi";
import { Hash } from "viem";
import useSanction from "@hooks/useSanction";
import SanctionBanner from "@components/SanctionBanner";

const getTransactionInitialState = () => ({
  open: false,
  hash: "",
  error: "",
  hasUserDenied: false,
});

const ClaimEL = () => {
  const { chain } = useDappChain();
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  const { writeContract } = useWriteContract();

  const [isTxModalOpen, setIsTxModalOpen] = useState(
    getTransactionInitialState()
  );

  const [pendingTx, setPendingTx] = useState(false);

  const { isLoading, data } = useClaims();

  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const [claimType, setClaimType] = useState<ClaimType>();

  const handleClaim = (data: Airdrop) => {
    setIsDisclaimerModalOpen(true);
    setClaimType(data.type);
  };

  const onClaimSubmit = () => {
    if (isLoading || !publicClient) return;

    const airdrop = data.find((d) => d.data?.type === claimType);

    if (!airdrop?.data) {
      return;
    }

    setIsTxModalOpen({
      ...isTxModalOpen,
      open: true,
    });
    setPendingTx(true);

    console.log({
      abi: airdrop.data.contract.abi,
      address: airdrop.data.contract.address,
      functionName: "claim",
      args: [
        airdrop?.data.currentIndex,
        address,
        airdrop.data.totalAmount,
        airdrop.data.proofs,
      ],
    });

    writeContract(
      {
        address: airdrop.data.contract.address,
        abi: airdrop.data.contract.abi,
        functionName: "claim",
        args: [
          airdrop?.data.currentIndex,
          address,
          airdrop.data.totalAmount,
          airdrop.data.proofs,
        ],
      },
      {
        onError: (error) => {
          console.log({ error });

          // @ts-ignore
          const errorMessage = error.shortMessage || error.message;

          setIsTxModalOpen({
            open: true,
            // @ts-ignore
            hash: error.transactionHash,
            error: errorMessage,
            hasUserDenied: isErrorRejectedByUser(error),
          });
          trackEvent(
            airdrop.data?.type === ClaimType.SCROLL
              ? CLAIM_SCROLL_FAILED
              : CLAIM_EL_FAILED,
            {
              error: errorMessage,
              hasUserDenied: isErrorRejectedByUser(error),
              amount: airdrop.data?.availableAmount,
            }
          );
          setPendingTx(false);
        },
        onSuccess: async (hash: Hash) => {
          await publicClient?.waitForTransactionReceipt({
            hash,
          });

          airdrop.refetch();

          setPendingTx(false);
          trackEvent(
            airdrop.data?.type === ClaimType.SCROLL
              ? CLAIM_SCROLL_SUCCESSFUL
              : CLAIM_EL_SUCCESSFUL,
            {
              token_out_amount: airdrop.data?.availableAmount,
              hash,
              token_out_symbol: airdrop.data?.unit,
            }
          );
          setIsTxModalOpen({
            open: true,
            hash,
            error: "",
            hasUserDenied: false,
          });
        },
      }
    );
  };

  const claimedAirdrops = data.filter(
    (d) => (d.data?.claimedAmount || 0n) > 0n
  );
  const { isSanctioned } = useSanction();
  return (
    <>
      <Flex justifyContent="center">
        <Flex width="95%">
          {isSanctioned && <SanctionBanner />}
        </Flex>
      </Flex>
      <Flex
        padding={{ base: "1rem", md: "10px 2rem" }}
        direction="column"
        gap="2.5rem"
        pointerEvents={isSanctioned ? "none" : "auto"}
        opacity={isSanctioned ? 0.5 : 1}
      >
        <Flex flexDirection="column" gap="1.5rem">
          <Heading fontSize="32px" lineHeight="38px" fontWeight={600}>
            Claims
          </Heading>
          <Text fontWeight={400} fontSize="14px" lineHeight="20px">
            Claim your airdrop quickly and securely. For more information, you
            can read the FAQ{" "}
            <Link
              isExternal
              href="https://kelp.gitbook.io/kelp/claim-tokens"
              textDecoration="underline"
              textUnderlineOffset="4px"
              color="#6E9BDE"
            >
              here.
            </Link>
          </Text>
        </Flex>
        {
          <ActiveAirDrops
            airdrops={data as ClaimData[]}
            handleClaim={handleClaim}
            isLoading={isLoading}
          />
        }
        {isConnected && (
          <Flex gap="2.5rem" direction={{ base: "column", md: "row" }}>
            {airdrops.futureAirdrops.length > 0 && (
              <FutureAirdrops airdrops={airdrops.futureAirdrops} />
            )}
            <ClaimedAirdrops
              airdrops={claimedAirdrops as ClaimData[]}
              isLoading={isLoading}
            />
          </Flex>
        )}
      </Flex>

      {isDisclaimerModalOpen && claimType && (
        <ClaimELDisclaimer
          isOpen={isDisclaimerModalOpen}
          onClose={() => {
            trackEvent(CLAIM_DISCLAIMER_REJECT);
            setIsDisclaimerModalOpen(false);
          }}
          onApprove={() => {
            trackEvent(CLAIM_DISCLAIMER_APPROVE);
            onClaimSubmit();
            setIsDisclaimerModalOpen(false);
          }}
          type={claimType}
        />
      )}

      <TransactionModal
        isOpen={isTxModalOpen.open}
        hash={isTxModalOpen.hash}
        error={isTxModalOpen.error}
        transactionType={TXN_TYPES.CLAIM_EL_POINTS}
        isTxnProcessing={pendingTx}
        hasUserDenied={isTxModalOpen.hasUserDenied}
        closeAlert={() => setIsTxModalOpen(getTransactionInitialState())}
        handleTxView={() => {
          const url = `${chain.blockExplorers?.default.url}/tx/${isTxModalOpen.hash}`;
          window.open(url, "_blank");
        }}
        chainId={chain.id}
      />
    </>
  );
};

export default ClaimEL;
